import { Camera, ClipboardX, CloudUpload, FileDown, Search } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { NonConformityRow } from '@obraplan/core/application/use-cases/quality/non-conformity-use-cases';
import { validationError } from '@obraplan/core/application/errors/app-error';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { QUALITY_ERROR } from '@obraplan/core/domain/quality/errors';
import { asId } from '@obraplan/core/domain/shared/ids';
import { err } from '@obraplan/core/domain/shared/result';
import { semResposta } from '@/app/conference-operations';
import { ncNaFila } from '@/app/outbox-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { form30FileName, form30Sheet } from '@/features/quality/form-30';
import { NonConformityDetail } from '@/features/quality/NonConformityDetail';
import { ncsComAFila, syncDaNc } from '@/features/quality/fila-das-ncs';
import { AvisoDaFila } from '@/features/planning/AvisoDaFila';
import { GUARDADO_NO_APARELHO } from '@/features/planning/fila-da-conferencia';
import {
  estaNaFila,
  galeriaComAFila,
  removerAFoto,
  urlDaFotoNaFila,
} from '@/features/planning/fotos-na-fila';
import { useFilaPorCima } from '@/features/planning/use-fila-por-cima';
import {
  NON_CONFORMITY_FILTERS,
  countByFilter,
  daysBetween,
  formatNcQuantity,
  openFor,
  todayBusinessDate,
  visibleRows,
  type NonConformityFilter,
} from '@/features/quality/non-conformity-list';
import { downloadXlsx, toXlsx } from '@/features/reports/xlsx';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { cn } from '@/lib/cn';
import { formatShortDate } from '@/lib/format';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/** O mesmo chip de filtro da vista de materiais do Cronograma. */
const CHIP_CLASS =
  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors aria-pressed:border-(--color-brand) aria-pressed:bg-(--color-brand-soft) aria-pressed:text-(--color-content) border-(--color-border-subtle) bg-(--color-surface-raised) text-(--color-content-muted) hover:text-(--color-content)';

/** O trilho à esquerda da linha. A cor nunca vem só: a situação também está escrita. */
const RAIL: Readonly<Record<NonConformityRow['status'], string>> = {
  OPEN: 'border-l-(--color-state-warn)',
  RESOLVED: 'border-l-(--color-state-ok)',
  CANCELLED: 'border-l-(--color-border-subtle)',
};

function plural(n: number, um: string, varios: string): string {
  return `${String(n)} ${n === 1 ? um : varios}`;
}

/** "a mais antiga de 10/10, há 12 dias" — o que está esperando há mais tempo. */
function maisAntiga(rows: readonly NonConformityRow[], today: string): string | undefined {
  const datas = rows.filter((row) => row.status === 'OPEN').map((row) => row.date);
  if (datas.length === 0) return undefined;
  const primeira = datas.sort()[0]!;
  const dias = Math.max(daysBetween(primeira, today), 0);
  const quando = dias === 0 ? 'de hoje' : dias === 1 ? 'de ontem' : `há ${String(dias)} dias`;
  return datas.length === 1 ? quando : `a mais antiga ${quando}`;
}

/**
 * Não conformidades — o FORM.30 da obra, dentro do sistema (Fase 37, etapa 2 — RN-481 a 486).
 *
 * A NC nasce na medição, quando um item da checklist é reprovado; aqui ela é tratada. A lista
 * abre nas **abertas**, da mais antiga para a mais nova — é o que está esperando alguém. Clicar
 * abre o detalhe da linha, um por vez: encerrar com a solução, cancelar a aberta por engano e
 * pôr fotos. O FORM.30 sai inteiro, com todas as NCs da obra, para o SGQ.
 *
 * A permissão da tela traz a de tratar as NCs (`non-conformities.manage`, D8): quem abre a aba
 * encerra.
 */
export function NonConformitiesPage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const canManage = usePermission(
    PERMISSION.NON_CONFORMITIES_MANAGE,
    activeProjectId === undefined ? undefined : asId<'Project'>(activeProjectId),
  );
  const canRemovePhoto = usePermission(
    PERMISSION.LABOR_MODIFY,
    activeProjectId === undefined ? undefined : asId<'Project'>(activeProjectId),
  );

  const [filter, setFilter] = useState<NonConformityFilter>('OPEN');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | undefined>(undefined);
  // Outra pessoa mexeu na NC enquanto esta tela escrevia: a lista é lida de novo.
  const [revisao, setRevisao] = useState(0);
  const today = todayBusinessDate();

  const view = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.listProjectNonConformities.execute({ projectId: activeProjectId }, session),
    [session?.userId, activeProjectId, revisao],
  );

  // A fila do aparelho nesta obra (Fase 43, RN-623): a linha já mostra o que foi feito sem sinal.
  const fila = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve([])
        : container.outbox.list(session.userId, { projectId: activeProjectId }),
    [session?.userId, activeProjectId],
  );
  const entradas = useFilaPorCima(fila, view);

  const photos = useLive(async () => {
    if (session === undefined || expanded === undefined) return undefined;
    const lido = await container.listNonConformityPhotos.execute(
      { nonConformityId: expanded },
      session,
    );
    if (lido.ok) return { id: expanded, ...lido.value };
    // Sem sinal e sem a leitura guardada, a galeria abre com o que o aparelho tem: as fotos
    // tiradas agora entram na fila e aparecem nela (RN-622).
    return semResposta(lido.error)
      ? { id: expanded, attachments: [], usage: undefined }
      : undefined;
  }, [session?.userId, expanded]);

  const resolveUrl = useCallback(
    async (attachmentId: string): Promise<string | undefined> => {
      if (estaNaFila(attachmentId)) return urlDaFotoNaFila(attachmentId, fila ?? []);
      if (session === undefined) return undefined;
      const result = await container.getAttachmentUrl.execute({ attachmentId }, session);
      return result.ok ? result.value : undefined;
    },
    [container, session, fila],
  );

  const recarregarSeConflito = (code: string | undefined) => {
    if (code === QUALITY_ERROR.NON_CONFORMITY_VERSION_CONFLICT) setRevisao((n) => n + 1);
  };

  // Encerrar, pôr e tirar foto vão pela fila do aparelho, na chave da NC (RN-623): sobem na hora,
  // ou na volta do sinal, na ordem em que foram feitos. Cancelar continua pedindo sinal.
  const doDia = (row: NonConformityRow) =>
    ({ projectId: activeProjectId!, date: row.date, resourceId: ncNaFila(row.id) }) as const;
  const [resolve, resolveState] = useCommand((row: NonConformityRow, solution: string) =>
    container.outbox.submit(
      {
        kind: 'resolver-nc',
        ...doDia(row),
        nonConformityId: row.id,
        solution,
        expectedVersion: row.version,
      },
      session!,
    ),
  );
  const [cancel, cancelState] = useCommand((row: NonConformityRow, reason: string) =>
    container.cancelNonConformity.execute(
      { nonConformityId: row.id, reason, expectedVersion: row.version },
      session!,
    ),
  );
  const [attach, attachState] = useCommand(
    (row: NonConformityRow, files: readonly File[]) =>
      files.length === 0
        ? Promise.resolve(err(validationError('INPUT.INVALID', 'Nenhum arquivo escolhido.')))
        : container.outbox.submit(
            { kind: 'fotos-da-nc', ...doDia(row), nonConformityId: row.id, files },
            session!,
          ),
    {
      successMessage: (resultado) =>
        resultado.queued ? GUARDADO_NO_APARELHO : 'Foto da não conformidade anexada.',
    },
  );
  const [removePhoto, removeState] = useCommand(
    (row: NonConformityRow, attachmentId: string) =>
      removerAFoto(container.outbox, session!, attachmentId, doDia(row)),
    {
      successMessage: (resultado) => (resultado.queued ? GUARDADO_NO_APARELHO : 'Foto removida.'),
    },
  );

  const rows = useMemo(
    () =>
      ncsComAFila(
        view !== undefined && view.ok ? view.value.rows : [],
        entradas,
        session?.userName ?? '',
      ),
    [view, entradas, session?.userName],
  );
  const counts = countByFilter(rows);
  const visiveis = useMemo(() => visibleRows(rows, filter, query), [rows, filter, query]);
  // Com mais de uma torre, o local leva a torre na frente: "3º Pavimento" sozinho seria dois.
  const variasTorres = useMemo(
    () =>
      new Set(rows.flatMap((row) => (row.towerName === undefined ? [] : [row.towerName]))).size > 1,
    [rows],
  );

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para ver as não conformidades dela."
        icon={<ClipboardX className="size-8" />}
      />
    );
  }
  if (view === undefined) return <LoadingState label="Carregando as não conformidades…" />;
  if (!view.ok) return <ErrorState message={view.error.message} code={view.error.code} />;

  const exportar = () => {
    try {
      downloadXlsx(form30FileName(view.value.projectName), toXlsx(form30Sheet(view.value)));
    } catch (causa) {
      console.error('[obraplan] falha ao gerar o FORM.30', causa);
      toast.error('Não foi possível gerar a planilha do FORM.30.');
    }
  };

  const antiga = maisAntiga(rows, today);
  const pendente =
    resolveState.isPending ||
    cancelState.isPending ||
    attachState.isPending ||
    removeState.isPending;

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-semibold">Não conformidades</h1>
          <p className="text-(--color-content-muted)">
            O que a medição reprovou na obra, a solução e a reinspeção — o FORM.30, aqui dentro.
          </p>
        </div>
        <Button variant="secondary" disabled={rows.length === 0} onClick={exportar}>
          <FileDown aria-hidden="true" />
          Exportar FORM.30
        </Button>
      </header>

      {rows.length === 0 ? (
        <EmptyState
          title="Nenhuma não conformidade nesta obra"
          description="Elas nascem na medição, quando um item da checklist é reprovado, e chegam aqui para serem tratadas."
          icon={<ClipboardX className="size-8" />}
        />
      ) : (
        <>
          <p className="text-sm text-(--color-content-muted)" data-testid="resumo-das-ncs">
            <span className="font-medium text-(--color-content)">
              {plural(counts.OPEN, 'aberta', 'abertas')}
            </span>
            {antiga === undefined ? '' : `, ${antiga}`} ·{' '}
            {plural(counts.RESOLVED, 'resolvida', 'resolvidas')} ·{' '}
            {plural(counts.CANCELLED, 'cancelada', 'canceladas')}
          </p>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Situação">
              {NON_CONFORMITY_FILTERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={filter === item.id}
                  className={CHIP_CLASS}
                  onClick={() => {
                    setFilter(item.id);
                    setExpanded(undefined);
                  }}
                >
                  {item.label}
                  <span className="tabular">{counts[item.id]}</span>
                </button>
              ))}
            </div>
            <label className="relative w-full sm:w-72">
              <span className="sr-only">Procurar não conformidade</span>
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-(--color-content-muted)"
              />
              <Input
                type="search"
                className="pl-9"
                placeholder="Nº, local, serviço ou texto"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
          </div>

          {visiveis.length === 0 ? (
            <EmptyState
              title={
                query.trim() !== ''
                  ? `Nada encontrado para “${query.trim()}”`
                  : filter === 'OPEN'
                    ? 'Nenhuma não conformidade aberta'
                    : 'Nenhuma não conformidade aqui'
              }
              description={
                query.trim() !== ''
                  ? 'A busca olha o número, o local, o serviço, o item e o que foi escrito.'
                  : 'As outras situações estão nos filtros acima.'
              }
              icon={<ClipboardX className="size-8" />}
            />
          ) : (
            <ul
              aria-label="Não conformidades"
              className="divide-y divide-(--color-border-subtle) overflow-hidden rounded-lg border border-(--color-border-subtle) bg-(--color-surface-raised)"
            >
              {visiveis.map((row) => {
                const aberto = expanded === row.id;
                const quantidade = formatNcQuantity(row);
                const sync = syncDaNc(entradas, row.id);
                const local =
                  variasTorres && row.towerName !== undefined
                    ? `${row.towerName} · ${row.localLabel}`
                    : row.localLabel;
                return (
                  <li key={row.id} className={cn('border-l-[3px]', RAIL[row.status])}>
                    <button
                      type="button"
                      aria-expanded={aberto}
                      aria-controls={`nc-detalhe-${row.id}`}
                      onClick={() => setExpanded(aberto ? undefined : row.id)}
                      className="grid w-full grid-cols-[3.25rem_minmax(0,1fr)] gap-x-3 gap-y-2 px-3 py-3 text-left transition-colors hover:bg-(--color-surface-muted) sm:grid-cols-[3.75rem_minmax(0,1fr)_auto] md:px-4"
                    >
                      <span
                        className="flex flex-col leading-none"
                        aria-label={`NC nº ${String(row.number)}`}
                      >
                        <span className="text-[11px] text-(--color-content-muted)">nº</span>
                        <span
                          className={cn(
                            'font-condensed text-3xl font-semibold tabular',
                            row.status === 'CANCELLED' &&
                              'text-(--color-content-muted) line-through decoration-1',
                          )}
                        >
                          {row.number}
                        </span>
                      </span>
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-medium break-words">{row.description}</span>
                        <span className="text-sm break-words text-(--color-content-muted)">
                          {local} · {row.serviceName}
                        </span>
                        <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-(--color-content-muted)">
                          <span>{row.itemLabel}</span>
                          <span aria-hidden="true">·</span>
                          <span>{formatShortDate(row.date)}</span>
                          {row.ownerName !== undefined && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span>{row.ownerName}</span>
                            </>
                          )}
                          {quantidade !== undefined && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span className="tabular">{quantidade} não conformes</span>
                            </>
                          )}
                          {row.places.length > 0 && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span>{row.places.map((place) => place.label).join(', ')}</span>
                            </>
                          )}
                          {row.photoCount > 0 && (
                            <>
                              <span aria-hidden="true">·</span>
                              <span className="inline-flex items-center gap-1">
                                <Camera aria-hidden="true" className="size-3.5" />
                                {plural(row.photoCount, 'foto', 'fotos')}
                              </span>
                            </>
                          )}
                        </span>
                      </span>
                      <span className="col-start-2 flex flex-wrap items-center gap-1.5 sm:col-start-3 sm:row-start-1 sm:justify-self-end">
                        {row.status === 'OPEN' ? (
                          <Badge tone="warn">{openFor(row.date, today)}</Badge>
                        ) : row.status === 'RESOLVED' ? (
                          <Badge tone="ok">Resolvida</Badge>
                        ) : (
                          <Badge tone="neutral">Cancelada</Badge>
                        )}
                        {sync !== undefined && (
                          <Badge
                            tone={sync.state === 'rejected' ? 'danger' : 'info'}
                            icon={<CloudUpload aria-hidden="true" />}
                          >
                            {sync.state === 'rejected' ? 'Não aceita' : 'Aguardando envio'}
                          </Badge>
                        )}
                      </span>
                    </button>
                    {aberto && (
                      <div id={`nc-detalhe-${row.id}`}>
                        {sync !== undefined && (
                          <div className="flex flex-col border-t border-(--color-border-subtle) bg-(--color-surface-sunken) px-4 pt-3 md:px-5">
                            <AvisoDaFila
                              sync={sync}
                              pendente={
                                sync.count === 1
                                  ? '1 alteração desta NC aguardando envio'
                                  : `${String(sync.count)} alterações desta NC aguardando envio`
                              }
                              recusado="O servidor não aceitou o que foi feito sem sinal nesta NC"
                            />
                          </div>
                        )}
                        <NonConformityDetail
                          row={row}
                          canManage={canManage}
                          canRemovePhoto={canRemovePhoto}
                          photos={
                            photos?.id === row.id
                              ? galeriaComAFila(photos, fila ?? [], {
                                  kind: 'nc',
                                  nonConformityId: row.id,
                                })
                              : undefined
                          }
                          isPending={pendente}
                          resolveUrl={resolveUrl}
                          onResolve={(solution) =>
                            void resolve(row, solution).then((result) => {
                              if (result?.ok === true) {
                                toast.success(
                                  result.value.queued
                                    ? GUARDADO_NO_APARELHO
                                    : `Não conformidade nº ${String(row.number)} encerrada.`,
                                );
                              } else recarregarSeConflito(result?.error.code);
                            })
                          }
                          onCancel={(reason) =>
                            void cancel(row, reason).then((result) => {
                              if (result?.ok === true) {
                                toast.success(
                                  `Não conformidade nº ${String(row.number)} cancelada.`,
                                );
                              } else recarregarSeConflito(result?.error.code);
                            })
                          }
                          onAttach={(files) => void attach(row, files)}
                          onRemovePhoto={(attachmentId) => void removePhoto(row, attachmentId)}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
